import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { stringify } from 'query-string';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { fetcher } from '@dashboard/services/request';
import {
  adminEmailTemplateLiveEmailKey,
  adminEmailTemplateLivePageKey,
  adminEmailTemplatePagesKey,
  adminEmailTemplateRenderKey,
  adminEmailTemplatesCatalogKey,
} from '@dashboard/lib/queryKeys';

export type MailAudience = 'customer' | 'seller' | 'staff';

export type CatalogTemplate = {
  template: string;
  layout: string;
  name: string;
  audience: MailAudience;
  group: string;
  sentWhen: string;
  previews: { id: string; variant: string }[];
};

export type RenderedTemplate = { subject: string; html: string };

export type PageSummary = {
  id: string;
  name: string;
  title?: string;
  slug?: string;
  type: string;
  status?: string;
  updatedAt?: string;
  seller: { id: string; name: string } | null;
};

export type LiveEmail = { key: string; label: string; path: string };

export type LiveCharge = {
  id: string;
  orderId?: number;
  amount: string;
  status?: string;
  livemode?: boolean;
  createdAt?: string;
  customer: string | null;
  emails: LiveEmail[];
};

export type LivePage = {
  page: PageSummary;
  prices: { id: string; label: string; billingType: string }[];
  emails: LiveEmail[];
  charges: LiveCharge[];
};

const BASE_PATH = '/admin/mail-templates';

// Every fetch renders templates on the server, so only Reload asks again. A template
// that throws fails the same way on a retry, so retrying only delays the error.
const RENDER_OPTIONS = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
} as const;

const withQuery = (path: string, query: Record<string, string | number | undefined>) => {
  const search = stringify(query, { skipEmptyString: true });
  return search ? `${path}?${search}` : path;
};

export function useAdminEmailTemplateCatalog() {
  const { token } = useAuth();

  return useQuery<CatalogTemplate[]>({
    queryKey: adminEmailTemplatesCatalogKey(),
    queryFn: () => fetcher(`${BASE_PATH}/catalog`, token),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useAdminEmailTemplateRender(previewId: string | undefined) {
  const { token } = useAuth();

  return useQuery<RenderedTemplate>({
    queryKey: adminEmailTemplateRenderKey(previewId),
    queryFn: () => fetcher(`${BASE_PATH}/render/${encodeURIComponent(previewId)}`, token),
    enabled: Boolean(previewId),
    ...RENDER_OPTIONS,
  });
}

export function useAdminEmailTemplatePages(
  q: string,
  includeTests: boolean,
  options: { enabled?: boolean } = {},
) {
  const { token } = useAuth();

  return useQuery<PageSummary[]>({
    queryKey: adminEmailTemplatePagesKey(q, includeTests),
    queryFn: () =>
      fetcher(withQuery(`${BASE_PATH}/pages`, { q, tests: includeTests ? 1 : undefined }), token),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });
}

export function useAdminEmailTemplateLivePage(pageId: string, priceId: string) {
  const { token } = useAuth();

  return useQuery<LivePage>({
    queryKey: adminEmailTemplateLivePageKey(pageId, priceId),
    queryFn: () =>
      fetcher(withQuery(`${BASE_PATH}/pages/${encodeURIComponent(pageId)}`, { priceId }), token),
    enabled: Boolean(pageId),
    // Keeps the page on screen while a new price loads, but never shows another page's data.
    placeholderData: previous => (previous?.page.id === pageId ? previous : undefined),
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useAdminEmailTemplateLiveEmail(path: string | undefined) {
  const { token } = useAuth();

  return useQuery<{ html: string }>({
    queryKey: adminEmailTemplateLiveEmailKey(path),
    queryFn: () => fetcher(`${BASE_PATH}${path}`, token),
    enabled: Boolean(path),
    ...RENDER_OPTIONS,
  });
}
